import React, { useEffect, useState } from 'react';
import { recipes } from '../data/recipes';
import { Clock, ExternalLink, Play, Youtube } from 'lucide-react';
import { motion } from 'framer-motion';
function useCountdown() {
  const [timeLeft, setTimeLeft] = useState('');
  useEffect(() => {
    const tick = () => {
      const now = new Date();
      const midnight = new Date();
      midnight.setHours(24, 0, 0, 0);
      const diff = midnight.getTime() - now.getTime();
      const h = Math.floor(diff / 3600000).toString().padStart(2, '0');
      const m = Math.floor(diff % 3600000 / 60000).toString().padStart(2, '0');
      const s = Math.floor(diff % 60000 / 1000).toString().padStart(2, '0');
      setTimeLeft(`${h}:${m}:${s}`);
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);
  return timeLeft;
}
export function RecipeOfTheDay() {
  const dayOfYear = Math.floor((Date.now() - new Date(new Date().getFullYear(), 0, 0).getTime()) / 86400000);
  const recipe = recipes[dayOfYear % recipes.length];
  const countdown = useCountdown();
  return <section id="recipe-of-day" className="py-20 px-4 bg-white" data-id="element-224">
      <div className="max-w-6xl mx-auto" data-id="element-225">
        <div className="text-center mb-10" data-id="element-226">
          <h2 className="text-3xl md:text-5xl font-bold text-stone-900" style={{
          fontFamily: 'Poppins, sans-serif'
        }} data-id="element-227">
            What's Cooking Today? 📅
          </h2>
          <p className="text-stone-500 mt-2" data-id="element-228">A fresh recipe every single day</p>
        </div>

        <motion.div initial={{
        opacity: 0,
        y: 30
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.5
      }} className="bg-[#fffbf5] rounded-3xl overflow-hidden shadow-2xl border border-stone-100 grid md:grid-cols-2" data-id="element-229">
          {/* Image */}
          <div className="relative aspect-video md:aspect-auto min-h-[300px] bg-stone-200" data-id="element-230">
            <img src={`https://img.youtube.com/vi/${recipe.videoId}/maxresdefault.jpg`} alt={recipe.name} className="w-full h-full object-cover" onError={e => {
            ;
            (e.target as HTMLImageElement).src = `https://img.youtube.com/vi/${recipe.videoId}/hqdefault.jpg`;
          }} data-id="element-231" />
            {/* Play button overlay */}
            <a href={recipe.videoUrl} target="_blank" rel="noopener noreferrer" className="absolute inset-0 flex items-center justify-center bg-black/20 hover:bg-black/30 transition-colors group" data-id="element-232">
              <div className="bg-red-600/90 p-5 rounded-full shadow-2xl transform group-hover:scale-110 transition-transform" data-id="element-233">
                <Play size={32} fill="white" data-id="element-234" />
              </div>
            </a>
            {/* Date badge */}
            <div className="absolute top-4 left-4 bg-orange-500 text-white px-4 py-1.5 rounded-full font-bold text-sm shadow-lg" data-id="element-235">
              📅 Today's Pick
            </div>
          </div>

          {/* Content */}
          <div className="p-8 md:p-10 flex flex-col justify-between" data-id="element-236">
            <div data-id="element-237">
              <div className="flex flex-wrap gap-2 mb-4" data-id="element-238">
                {recipe.categories.slice(0, 2).map(cat => <span key={cat} className="bg-green-100 text-green-700 text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wide" data-id="element-239">
                    {cat}
                  </span>)}
                {recipe.isNoOnionGarlic && <span className="bg-blue-100 text-blue-700 text-xs font-bold px-3 py-1 rounded-full" data-id="element-240">
                    Jain Friendly
                  </span>}
              </div>

              <h3 className="text-2xl md:text-3xl font-bold text-stone-900 mb-2 leading-tight" style={{
              fontFamily: 'Poppins, sans-serif'
            }} data-id="element-241">
                {recipe.name}
              </h3>
              {recipe.nameHindi && <p className="text-stone-400 text-sm mb-3" data-id="element-242">
                  {recipe.nameHindi}
                </p>}
              <p className="text-stone-600 mb-6 leading-relaxed" data-id="element-243">
                {recipe.description}
              </p>

              <div className="grid grid-cols-2 gap-3 mb-6" data-id="element-244">
                <div className="bg-white p-3 rounded-xl border border-stone-100" data-id="element-245">
                  <p className="text-xs text-stone-400 mb-1 flex items-center gap-1" data-id="element-246">
                    <Clock size={12} data-id="element-247" /> Cook Time
                  </p>
                  <p className="font-bold text-stone-900" data-id="element-248">
                    {recipe.cookTime} mins
                  </p>
                </div>
                <div className="bg-white p-3 rounded-xl border border-stone-100" data-id="element-249">
                  <p className="text-xs text-stone-400 mb-1" data-id="element-250">Cuisine</p>
                  <p className="font-bold text-stone-900" data-id="element-251">{recipe.cuisine}</p>
                </div>
              </div>

              {/* Ingredients preview */}
              <div className="mb-6" data-id="element-252">
                <p className="text-xs font-bold text-stone-400 uppercase tracking-wider mb-2" data-id="element-253">
                  Key Ingredients
                </p>
                <div className="flex flex-wrap gap-2" data-id="element-254">
                  {recipe.ingredients.map(ing => <span key={ing} className="bg-stone-100 text-stone-600 text-xs px-2.5 py-1 rounded-lg" data-id="element-255">
                      {ing}
                    </span>)}
                </div>
              </div>
            </div>

            {/* CTA + Countdown */}
            <div data-id="element-256">
              <a href={recipe.videoUrl} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2 w-full bg-red-600 hover:bg-red-700 text-white font-bold py-4 rounded-xl transition-all hover:scale-[1.02] shadow-lg shadow-red-600/20 mb-4" data-id="element-257">
                <Youtube size={20} data-id="element-258" /> Watch on YouTube
              </a>
              <div className="text-center bg-stone-50 rounded-xl py-3 border border-stone-100" data-id="element-259">
                <p className="text-xs text-stone-400 mb-1" data-id="element-260">Next recipe in</p>
                <p className="font-bold text-stone-700 text-xl tracking-widest font-mono" data-id="element-261">
                  {countdown}
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>;
}